import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [utilisateur, setUtilisateur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nbAlertes, setNbAlertes] = useState(0);
  const wsRef = useRef(null);
  const fallbackRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("utilisateur");
    if (token && userData) {
      setUtilisateur(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // ── WebSocket global — se connecte une seule fois ──
  useEffect(() => {
    if (!utilisateur) return;

    // Fermer l'ancienne connexion si elle existe
    if (wsRef.current) wsRef.current.close();
    if (fallbackRef.current) clearInterval(fallbackRef.current);

    const fetchAlertes = async () => {
      try {
        if (utilisateur.role === "Commercial") {
          const res = await api.get(`/alertes/commercial/${utilisateur.commercialId}/non-lues`);
          setNbAlertes(res.data.count || 0);
        } else if (utilisateur.role === "Superviseur") {
          const res = await api.get(`/alertes?lu=false`);
          setNbAlertes(res.data.length || 0);
        }
      } catch (_) {}
    };

    fetchAlertes();

    // Connexion WebSocket selon le rôle
    let wsUrl = null;
    if (utilisateur.role === "Commercial" && utilisateur.commercialId) {
      wsUrl = `ws://localhost:4000?commercialId=${utilisateur.commercialId}`;
    } else if (utilisateur.role === "Superviseur" && utilisateur.id) {
      wsUrl = `ws://localhost:4000?superviseurId=${utilisateur.id}`;
    }

    if (!wsUrl) return;

    let fermetureIntentionnelle = false;

    const connectWs = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "NOUVELLE_ALERTE") {
            setNbAlertes(prev => prev + 1);
          }
        } catch (_) {}
      };

      ws.onerror = () => {
        if (!fallbackRef.current) {
          fallbackRef.current = setInterval(fetchAlertes, 5000);
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!fermetureIntentionnelle) {
          setTimeout(connectWs, 3000);
        }
      };
    };

    connectWs();

    return () => {
      fermetureIntentionnelle = true;
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (fallbackRef.current) { clearInterval(fallbackRef.current); fallbackRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur?.id]);

  const login = async (email, mot_de_passe) => {
    const { data } = await api.post("/auth/login", { email, mot_de_passe });
    localStorage.setItem("token", data.token);
    localStorage.setItem("utilisateur", JSON.stringify(data.utilisateur));
    setUtilisateur(data.utilisateur);
    return data.utilisateur;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) await api.post("/auth/logout", { token });
    } catch (_) {}
    // Fermer WebSocket proprement
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (fallbackRef.current) { clearInterval(fallbackRef.current); fallbackRef.current = null; }
    localStorage.removeItem("token");
    localStorage.removeItem("utilisateur");
    setUtilisateur(null);
    setNbAlertes(0);
  };

  const resetNbAlertes = () => setNbAlertes(0);

  return (
    <AuthContext.Provider value={{ utilisateur, login, logout, loading, nbAlertes, setNbAlertes, resetNbAlertes }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}